import type {
  CommandEmptyProps,
  CommandHeaderProps,
  CommandItemProps,
  CommandListProps,
  CommandProps,
} from '../../../core'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandHeader,
  CommandHeaderContent,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Ellipsis,
  Redact,
  Tooltip,
} from '../../../core'
import type { components } from '@joyful/openapi/types.gen'
import type { CompositionContextValue } from '@joyful/utils'
import { createNonNullableContext } from '@joyful/utils'
import { WrenchIcon } from 'lucide-react'
import type { ReactElement, ReactNode } from 'react'
import type { SetOptional } from 'type-fest'

import { useHasUserCapability } from '../../hooks/useHasUserCapability'

type AccountRead = components['schemas']['AccountRead']
type CurrentUserAccountRead = components['schemas']['CurrentUserAccountRead']

export type AccountSelectionCommandAccount = Pick<
  AccountRead,
  'id' | 'is_test_account' | 'slug'
> &
  Partial<Pick<AccountRead, 'name'>> &
  Partial<Pick<CurrentUserAccountRead, 'is_active'>>

interface AccountSelectionCommandBaseState {
  accounts: Array<AccountSelectionCommandAccount>
  disabledAccountIds?: Array<string>
  isDisabled: boolean
  /**
   * @default 'md'
   */
  size: 'sm' | 'md' | 'lg'
}

export interface AccountSelectionCommandState extends AccountSelectionCommandBaseState {
  activeAccount: AccountSelectionCommandAccount | null
}

export interface AccountSelectionCommandActions {
  onSelect: (account: AccountSelectionCommandAccount | null) => void
}

export interface AccountMultiSelectionCommandState extends AccountSelectionCommandBaseState {
  selectedAccountIds: Array<string>
}

export interface AccountMultiSelectionCommandActions {
  onToggle: (account: AccountSelectionCommandAccount) => void
}

type AccountSelectionCommandContextValue =
  | (CompositionContextValue<
      AccountSelectionCommandState,
      AccountSelectionCommandActions
    > & { selectionVariant: 'single' })
  | (CompositionContextValue<
      AccountMultiSelectionCommandState,
      AccountMultiSelectionCommandActions
    > & { selectionVariant: 'multi' })

const AccountSelectionCommandContext =
  createNonNullableContext<AccountSelectionCommandContextValue>(
    'AccountSelectionCommandContext',
  )

const useAccountSelectionCommandContext =
  AccountSelectionCommandContext.useRequiredContext

export interface AccountSelectionCommandProps extends CommandProps {
  actions: AccountSelectionCommandActions
  state: AccountSelectionCommandState
}

export function AccountSelectionCommand({
  actions,
  children,
  state,
  ...props
}: AccountSelectionCommandProps) {
  return (
    <AccountSelectionCommandContext.Context
      value={{
        actions,
        selectionVariant: 'single',
        state,
      }}
    >
      <Command {...props}>{children}</Command>
    </AccountSelectionCommandContext.Context>
  )
}

export interface AccountMultiSelectionCommandProps extends CommandProps {
  actions: AccountMultiSelectionCommandActions
  state: AccountMultiSelectionCommandState
}

export function AccountMultiSelectionCommand({
  actions,
  children,
  state,
  ...props
}: AccountMultiSelectionCommandProps) {
  return (
    <AccountSelectionCommandContext.Context
      value={{
        actions,
        selectionVariant: 'multi',
        state,
      }}
    >
      <Command {...props}>{children}</Command>
    </AccountSelectionCommandContext.Context>
  )
}

export interface AccountSelectionCommandHeaderProps extends Omit<
  CommandHeaderProps,
  'children'
> {
  subtitle?: ReactNode
}

export function AccountSelectionCommandHeader({
  subtitle,
  ...props
}: AccountSelectionCommandHeaderProps) {
  const context = useAccountSelectionCommandContext()

  if (
    context.selectionVariant === 'multi' ||
    context.state.activeAccount === null
  ) {
    return null
  }

  return (
    <CommandHeader justify="between" padding="md" {...props}>
      <CommandHeaderContent>
        <Ellipsis asChild tone="strong" size="sm" weight="medium">
          <Redact>
            {context.state.activeAccount.name ??
              context.state.activeAccount.slug}
          </Redact>
        </Ellipsis>
        {subtitle ? (
          <p className="text-tremor-content text-xs">{subtitle}</p>
        ) : null}
      </CommandHeaderContent>
    </CommandHeader>
  )
}

export const AccountSelectionCommandGroup = CommandGroup
export const AccountSelectionCommandSeparator = CommandSeparator

function AccountSelectionCommandEmpty({
  children,
  ...props
}: SetOptional<CommandEmptyProps, 'children'>) {
  return (
    <CommandEmpty {...props}>
      {children ?? 'No matching accounts.'}
    </CommandEmpty>
  )
}

export const AccountSelectionCommandInput = CommandInput

export interface AccountSelectionCommandOptionProps extends Omit<
  CommandItemProps,
  'children' | 'disabled' | 'isSelected'
> {
  account: AccountSelectionCommandAccount
  isDisabled?: boolean
  isSelected?: boolean
}

export function AccountSelectionCommandOption({
  account,
  isDisabled,
  isSelected,
  onSelect,
  size,
  ...props
}: AccountSelectionCommandOptionProps) {
  const hasDebugCapability = useHasUserCapability('admin.debug')

  return (
    <CommandItem
      disabled={isDisabled}
      isSelected={isSelected}
      subtitle={
        hasDebugCapability ? (
          <p className="text-tremor-content-subtle inline-flex items-center gap-x-1.5 text-xs">
            {account.slug}
            {account.is_test_account ? (
              <Tooltip content="Test account">
                <WrenchIcon className="size-3.5" />
              </Tooltip>
            ) : null}
          </p>
        ) : undefined
      }
      keywords={account.slug ? [account.slug] : undefined}
      size={size}
      value={props.value ?? account.name ?? account.slug ?? account.id}
      onSelect={onSelect}
      {...props}
    >
      <Redact>{account.name ?? account.slug}</Redact>
    </CommandItem>
  )
}

function AccountSelectionCommandItem({
  account,
  onSelect,
  ...props
}: Omit<AccountSelectionCommandOptionProps, 'isDisabled' | 'isSelected'>) {
  const hasDebugCapability = useHasUserCapability('admin.debug')
  const context = useAccountSelectionCommandContext()
  let size: CommandItemProps['size']
  if (context.state.size === 'lg') {
    size = hasDebugCapability ? 'lg' : 'xl'
  } else {
    size = hasDebugCapability ? 'md' : 'lg'
  }

  return (
    <AccountSelectionCommandOption
      account={account}
      isDisabled={
        context.state.isDisabled ||
        context.state.disabledAccountIds?.includes(account.id)
      }
      isSelected={_isSelectedAccount({
        account,
        context,
      })}
      size={size}
      onSelect={(value) => {
        if (context.selectionVariant === 'single') {
          context.actions.onSelect(account)
        } else {
          context.actions.onToggle(account)
        }

        onSelect?.(value)
      }}
      {...props}
    />
  )
}

export interface AccountSelectionCommandNullOptionProps {
  children: string
}

export function AccountSelectionCommandNullOption({
  children,
}: AccountSelectionCommandNullOptionProps) {
  const context = useAccountSelectionCommandContext()

  if (context.selectionVariant === 'multi') {
    return null
  }

  const accountSelectionContext = context

  return (
    <CommandItem
      forceMount
      isSelected={accountSelectionContext.state.activeAccount === null}
      onSelect={() => {
        accountSelectionContext.actions.onSelect(null)
      }}
    >
      {children}
    </CommandItem>
  )
}

export function AccountSelectionCommandOptions() {
  const context = useAccountSelectionCommandContext()

  if (context.state.accounts.length === 0) {
    return null
  }

  return context.state.accounts.map((account) => {
    return <AccountSelectionCommandItem key={account.id} account={account} />
  })
}

function _isActiveAccount(account: AccountSelectionCommandAccount): boolean {
  // Inactive is explicit false; missing is_active still counts as active.
  // oxlint-disable-next-line joyful/prefer-boolean-check
  return account.is_active !== false
}

function _isSelectedAccount({
  account,
  context,
}: {
  account: AccountSelectionCommandAccount
  context: AccountSelectionCommandContextValue
}) {
  if (context.selectionVariant === 'single') {
    return account.id === context.state.activeAccount?.id
  }

  return context.state.selectedAccountIds.includes(account.id)
}

export function AccountSelectionCommandGroupedOptions() {
  const context = useAccountSelectionCommandContext()

  if (context.state.accounts.length === 0) {
    return null
  }

  const activeAccounts: Array<AccountSelectionCommandAccount> = []
  const inactiveAccounts: Array<AccountSelectionCommandAccount> = []
  for (const account of context.state.accounts) {
    if (_isActiveAccount(account)) {
      activeAccounts.push(account)
    } else {
      inactiveAccounts.push(account)
    }
  }

  return (
    <>
      {activeAccounts.length > 0 ? (
        <CommandGroup>
          {activeAccounts.map((account) => {
            return (
              <AccountSelectionCommandItem key={account.id} account={account} />
            )
          })}
        </CommandGroup>
      ) : null}
      {inactiveAccounts.length > 0 ? (
        <CommandGroup heading="Inactive">
          {inactiveAccounts.map((account) => {
            return (
              <AccountSelectionCommandItem key={account.id} account={account} />
            )
          })}
        </CommandGroup>
      ) : null}
    </>
  )
}

export interface AccountSelectionCommandListProps extends CommandListProps {
  empty?: ReactElement
}

export function AccountSelectionCommandList({
  children,
  empty,
  ...props
}: AccountSelectionCommandListProps) {
  return (
    <CommandList {...props}>
      {children}
      {empty ?? <AccountSelectionCommandEmpty />}
    </CommandList>
  )
}
